import { useCallback, useEffect, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIntl } from '@zenbusiness/application-commons-intl';
import ErrorState from '@zenbusiness/zui/ErrorState';
import PageContainer from '@zenbusiness/zui/PageContainer';
import Tab from '@zenbusiness/zui/Tab';
import Text from '@zenbusiness/zui/Text';

import { RELEASE_ACCOUNT_SETTINGS_MFA } from '#client/constants/experimentsFlags';
import { accountSettingsProfile, accountSettingsSecurity } from '#client/constants/routes';
import { useAccountSettings } from '#client/hooks/useAccountSettings';
import useCurrentTab from '#client/hooks/useCurrentTab';
import { isSocialAuth0Id } from '#client/utils/isSocialAuth0Id';

import * as Styled from './AccountSettings.style';
import { AccountHolder } from './components/AccountHolder';
import { ConnectWithPartners } from './components/ConnectWithPartners';
import { ManagePasswordModal } from './components/ManagePasswordModal';
import { OptOutModal } from './components/OptOutModal';
import { SignInSecurity } from './components/SignInSecurity';

import { MODAL_PARAM_KEY } from '../Home/constants';

export const LOCALIZED_TEXT = {
  tabsAriaLabel: 'dashboard.accountSettings.tabs.ariaLabel',
  profileTab: 'dashboard.accountSettings.tabs.profile',
  signInSecurityTab: 'dashboard.accountSettings.tabs.signInSecurity',
};

function AccountSettings() {
  const { formatMessage: fm } = useIntl();
  const { data: accountInfo, error, loading, refetch } = useAccountSettings();

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentModal, setCurrentModal] = useState(null);
  const { allPartnerOptOut, userAuth0Id } = accountInfo;
  const isSocialLogin = isSocialAuth0Id(userAuth0Id);
  const isMfaReleased = useExperimentFlag(RELEASE_ACCOUNT_SETTINGS_MFA, false);

  const navigate = useNavigate();
  const activeTab = useCurrentTab([accountSettingsProfile, accountSettingsSecurity], accountSettingsProfile);
  const showSecurity = isMfaReleased && activeTab === accountSettingsSecurity;

  const handleOpenModal = useCallback(
    (modal) => {
      setCurrentModal(modal);

      // Password lives under Sign-in & security, so a deep link to it opens that tab too.
      // Replace the entry so Back doesn't return to the old URL and reopen the modal.
      if (isMfaReleased && modal === 'managePassword' && activeTab !== accountSettingsSecurity) {
        navigate(
          {
            pathname: accountSettingsSecurity,
            search: `?${MODAL_PARAM_KEY}=${modal}`,
          },
          { replace: true },
        );
        return;
      }

      setSearchParams((params) => {
        params.set(MODAL_PARAM_KEY, modal);
        return params;
      });
    },
    [activeTab, isMfaReleased, navigate, setSearchParams],
  );

  const handleCloseModal = () => {
    setCurrentModal(null);
    setSearchParams((params) => {
      params.delete(MODAL_PARAM_KEY);
      return params;
    });
  };

  useEffect(() => {
    const modalName = searchParams.get(MODAL_PARAM_KEY);
    if (!modalName || currentModal) {
      return;
    }
    handleOpenModal(modalName);
  }, [currentModal, handleOpenModal, searchParams]);

  if (error) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <Styled.Header
        title={
          <Text component="p" size="2xl" family="secondary" weight="bold">
            Account settings
          </Text>
        }
      />
      {isMfaReleased && (
        <Styled.TabsNav value={activeTab} aria-label={fm({ id: LOCALIZED_TEXT.tabsAriaLabel })}>
          <Tab
            component={Link}
            label={fm({ id: LOCALIZED_TEXT.profileTab })}
            to={accountSettingsProfile}
            value={accountSettingsProfile}
          />
          <Tab
            component={Link}
            label={fm({ id: LOCALIZED_TEXT.signInSecurityTab })}
            to={accountSettingsSecurity}
            value={accountSettingsSecurity}
          />
        </Styled.TabsNav>
      )}
      {showSecurity ? (
        <SignInSecurity isSocialLogin={isSocialLogin} onClickManagePassword={() => handleOpenModal('managePassword')} />
      ) : (
        <Styled.AccountCard>
          {/* With the flag off there's no Sign-in & security tab, so password stays under General Info. */}
          <AccountHolder
            isSocialLogin={isSocialLogin}
            account={accountInfo}
            loading={loading}
            onClickEdit={isMfaReleased ? undefined : () => handleOpenModal('managePassword')}
          />
          <Styled.SectionDivider />
          <ConnectWithPartners
            account={accountInfo}
            loading={loading}
            onClickOptOut={() => handleOpenModal('optOut')}
          />
        </Styled.AccountCard>
      )}
      <ManagePasswordModal
        account={accountInfo}
        isOpen={currentModal === 'managePassword'}
        onClose={handleCloseModal}
      />
      {!allPartnerOptOut && (
        <OptOutModal isOpen={currentModal === 'optOut'} onClose={handleCloseModal} refresh={refetch} />
      )}
    </PageContainer>
  );
}

export default AccountSettings;
